import React, { useState } from 'react';
import {
  commitStudentImport,
  previewStudentImport,
  type SchoolCode,
  type StudentImportPreview,
} from '../api/axios';

interface Props {
  school: SchoolCode;
  onClose: () => void;
  onImported: () => void;
}

export const StudentImportModal: React.FC<Props> = ({ school, onClose, onImported }) => {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<StudentImportPreview | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);

  const analyze = async () => {
    if (!file) {
      setError('Choose a CSV file first.');
      return;
    }
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const data = await previewStudentImport(school, file);
      setPreview(data);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Could not read that file.');
    } finally {
      setBusy(false);
    }
  };

  const commit = async () => {
    if (!preview) return;
      const rows = preview.rows.filter((row) => row.ready).map((row) => ({
        first_name: row.data.first_name || '',
        last_name: row.data.last_name || '',
        gender: (row.data.gender || 'other') as 'male' | 'female' | 'other',
        date_of_birth: row.data.date_of_birth || '',
        form: Number(row.data.form),
        boarding_status:
          row.data.boarding_status === 'boarder' || row.data.boarding_status === 'day_scholar'
            ? row.data.boarding_status
            : undefined,
      }));
    if (rows.length === 0) {
      setError('No rows are ready to import.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const response = await commitStudentImport(school, rows);
      const failed = response.failed_count
        ? ` ${response.failed_count} row(s) were not created.`
        : '';
      setResult(`Created ${response.created_count} student(s). Default password is myschool1.${failed}`);
      setPreview(null);
      setFile(null);
      onImported();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Import failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000, padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: 24, width: 'min(920px, 100%)', maxHeight: '90vh', overflow: 'auto' }}>
        <h3 style={{ marginTop: 0 }}>Import students</h3>
        <p style={{ color: '#4b5563', lineHeight: 1.5 }}>
          CSV only. Required columns: first_name, last_name, gender (male, female, or other), date_of_birth (YYYY-MM-DD), form.
          Optional: boarding_status (boarder or day_scholar). Students are added to {school}.
        </p>
        <input
          type="file"
          accept=".csv,text/csv"
          onChange={(e) => {
            setFile(e.target.files?.[0] ?? null);
            setPreview(null);
            setResult(null);
          }}
        />
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button type="button" onClick={() => void analyze()} disabled={busy} style={buttonStyle}>
            {busy ? 'Working...' : 'Check file'}
          </button>
          <button type="button" onClick={onClose} style={{ ...buttonStyle, background: '#e5e7eb', color: '#111827' }}>
            Close
          </button>
        </div>
        {error && <p style={{ color: '#b91c1c' }}>{error}</p>}
        {result && <p style={{ color: '#047857' }}>{result}</p>}
        {preview && (
          <div style={{ marginTop: 16 }}>
            {preview.missing_headers.length > 0 && (
              <p style={{ color: '#b91c1c' }}>Missing required columns: {preview.missing_headers.join(', ')}</p>
            )}
            {preview.unused_columns.length > 0 && (
              <p style={{ color: '#92400e' }}>These columns will not be used: {preview.unused_columns.join(', ')}</p>
            )}
            <p>{preview.ready_count} row(s) are ready. Rows with problems or possible duplicates are left out.</p>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <thead>
                  <tr>
                    {['Line', 'Name', 'Form', 'Boarding', 'Result'].map((heading) => (
                      <th key={heading} style={{ textAlign: 'left', borderBottom: '1px solid #e5e7eb', padding: 8 }}>{heading}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((row) => (
                    <tr key={row.line}>
                      <td style={{ padding: 8 }}>{row.line}</td>
                      <td style={{ padding: 8 }}>{row.data.first_name} {row.data.last_name}</td>
                      <td style={{ padding: 8 }}>{row.data.form || '—'}</td>
                      <td style={{ padding: 8 }}>{row.data.boarding_status || '—'}</td>
                      <td style={{ padding: 8, color: row.ready ? '#047857' : '#b91c1c' }}>
                        {row.ready
                          ? 'Ready'
                          : [...row.problems, row.duplicate ? 'Possible duplicate (same name and date of birth)' : ''].filter(Boolean).join('; ')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button
              type="button"
              onClick={() => void commit()}
              disabled={busy || preview.ready_count === 0 || preview.missing_headers.length > 0}
              style={{ ...buttonStyle, marginTop: 12 }}
            >
              Import {preview.ready_count} ready student(s)
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const buttonStyle: React.CSSProperties = {
  padding: '10px 16px',
  background: '#1e3a5f',
  color: '#fff',
  border: 'none',
  borderRadius: 8,
  fontWeight: 600,
  cursor: 'pointer',
};
